/**
 * Router-agnostic router state store.
 *
 * Provides a tiny external store + event emitter shared by the Pages-router
 * and App-router compat providers so the `useCompatRouter` hook can return a
 * Pages-Router-shaped object regardless of which router is currently mounted.
 */

export interface RouterSnapshot {
  pathname: string;
  asPath: string;
  query: Record<string, string | string[] | undefined>;
  isReady: boolean;
}

type Listener = () => void;

let snapshot: RouterSnapshot = {
  pathname: "",
  asPath: "",
  query: {},
  isReady: false,
};

const listeners = new Set<Listener>();

export function setSnapshot(next: Partial<RouterSnapshot>): void {
  snapshot = { ...snapshot, ...next };
  listeners.forEach((listener) => listener());
}

export function getSnapshot(): RouterSnapshot {
  return snapshot;
}

export function subscribe(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

type EventHandler = (...args: any[]) => void;

const handlers: Record<string, Set<EventHandler>> = {};

export const routerEvents = {
  on(event: string, handler: EventHandler): void {
    if (!handlers[event]) handlers[event] = new Set();
    handlers[event].add(handler);
  },
  off(event: string, handler: EventHandler): void {
    handlers[event]?.delete(handler);
  },
  emit(event: string, ...args: any[]): void {
    handlers[event]?.forEach((handler) => {
      try {
        handler(...args);
      } catch (error) {
        console.error(`[compat-router] error in "${event}" handler:`, error);
      }
    });
  },
};

export function parseSearchToQuery(
  search: string,
): Record<string, string | string[]> {
  if (!search) return {};
  const params = new URLSearchParams(
    search.startsWith("?") ? search.slice(1) : search,
  );
  const query: Record<string, string | string[]> = {};
  params.forEach((value, key) => {
    const existing = query[key];
    if (existing === undefined) {
      query[key] = value;
    } else if (Array.isArray(existing)) {
      existing.push(value);
    } else {
      query[key] = [existing, value];
    }
  });
  return query;
}
